<template>
  <div class="content">
    <!-- 授权类型说明与引导到企业套餐 -->
    <PersonalAuthAlert />

    <!-- Segmented 切换个人入门版/个人标准版 -->
    <Segmented
      class="rights-segmented"
      :circle="true"
      :items="segmentedItems"
      :show-content="false"
      :model-value="selectedRightsTypeKey"
      @change="handleSegmentedChange"
    />

    <!-- 套餐价格卡片列表 -->
    <div class="fees-list">
      <div class="fees-list__grid">
        <Fees
          v-for="f in sortedFeesList"
          :key="f.id"
          :fees="f"
          :rightType="currentRightsType"
          :selected="f.id === selectedFee?.id"
          :discount="currentPlanAnnualSavings"
          @on-select="handleSelectFee(f, currentRightsType)"
        />
      </div>
    </div>

    <div>
      <CreditTip :selected-fee="selectedFee" />
      <!-- 订阅新套餐 连续订阅入门/标准 提示 -->
      <DiscountTip class="mt-[8px]" @add-coin="handleAddCoin" />
    </div>

    <ViewRightsCompare :personal-rights-types="personalRightsTypes" />
  </div>
</template>

<script setup lang="ts" name="PlanB">
import { getBuriedService } from '@rendora/common/services/buried/index';
import { getI18nService } from '@rendora/common/services/i18n';
import { type IFees, useRightsStore } from '@rendora/common/store/rights';
import { BUY_TYPE, IRightsType } from '@rendora/common/types/enums';
import { formatPrice } from '@rendora/common/utils/transform';
import Segmented, { type SegmentedItem } from '@rendora/design/components/segmented/Segmented.vue';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { filterFeesBySubscription } from '../../../constants';
import { usePayment } from '../../../store';
import DiscountTip from '../../discount-tip.vue';
import CreditTip from './CreditTip.vue';
import Fees from './Fees.vue';
import PersonalAuthAlert from './PersonalAuthAlert.vue';
import ViewRightsCompare from './ViewRightsCompare.vue';

const i18nService = getI18nService();
const $t = i18nService.t;

// 记忆每个权益类型对应的选中费用
const rememberedFees = ref<Partial<Record<IRightsType, IFees>>>({});

// Props
const props = defineProps<{
  selectedFee?: IFees;
  currentRightsType: IRightsType;
}>();

// Emits
const emit = defineEmits<{
  'update:selectedFee': [value: IFees | undefined];
  'update:currentRightsType': [value: IRightsType];
}>();

const paymentStore = usePayment();
// Store 初始化
const rightsStore = useRightsStore();
// 响应式引用
const { rightsDisplay } = storeToRefs(rightsStore);

// 个人版权益类型列表（用于 RightTable）
const personalRightsTypes = [IRightsType.PERSONAL_LITE, IRightsType.PERSONAL];

// Segmented 选项
const segmentedItems = computed<SegmentedItem[]>(() => [
  {
    key: String(IRightsType.PERSONAL_LITE),
    label: $t('ge-ren-ru-men-ban'),
  },
  {
    key: String(IRightsType.PERSONAL),
    label: $t('ge-ren-ban-biao-zhun-ban'),
  },
]);

// 当前选中的权益类型 key（用于 Segmented）
const selectedRightsTypeKey = computed(() => {
  // 如果 props.currentRightsType 是个人版类型，使用它；否则默认使用个人标准版
  if (
    props.currentRightsType === IRightsType.PERSONAL ||
    props.currentRightsType === IRightsType.PERSONAL_LITE
  ) {
    return String(props.currentRightsType);
  }
  return String(IRightsType.PERSONAL);
});

// 当前权益类型
const currentRightsType = computed(() => {
  return Number(selectedRightsTypeKey.value) as IRightsType;
});

// 权益信息：个人标准版
const personalPlanRights = computed(() =>
  rightsDisplay.value?.rights.find(r => r.rights_type === IRightsType.PERSONAL),
);

// 权益信息：个人入门版
const personalLitePlanRights = computed(() =>
  rightsDisplay.value?.rights.find(r => r.rights_type === IRightsType.PERSONAL_LITE),
);

// 当前选中的权益信息
const currentPlanRights = computed(() => {
  if (currentRightsType.value === IRightsType.PERSONAL) {
    return personalPlanRights.value;
  }
  return personalLitePlanRights.value;
});

// 排序后的费用列表：全款年套餐优先级最高，排在最前面
const sortedFeesList = computed(() => {
  const fees = filterFeesBySubscription(currentPlanRights.value?.rights_by_time || []);
  return [...fees].sort((a, b) => {
    // 全款年套餐（buy_type === PAYMENT && days === 365）优先级最高
    const aIsAnnualPayment = a.buy_type === BUY_TYPE.PAYMENT && a.days === 365;
    const bIsAnnualPayment = b.buy_type === BUY_TYPE.PAYMENT && b.days === 365;

    if (aIsAnnualPayment && !bIsAnnualPayment) return -1;
    if (!aIsAnnualPayment && bIsAnnualPayment) return 1;

    // 其他保持原有顺序
    return 0;
  });
});

// 年付相较月付节省金额（当前版本）
const currentPlanAnnualSavings = computed(() => {
  const costByMonth = currentPlanRights.value?.rights_by_time.find(
    v => v.days === 30 && v.buy_type === 0,
  );
  const costByYear = currentPlanRights.value?.rights_by_time.find(
    v => v.days === 365 && v.buy_type === 0,
  );
  if (costByMonth && costByYear) {
    return formatPrice(costByMonth?.price * 12 - costByYear.price).string;
  }
  return '';
});

// Segmented 切换处理
function handleSegmentedChange(value: string) {
  const newRightsType = Number(value) as IRightsType;
  const oldRightsType = props.currentRightsType;

  // 保存当前权益类型对应的选中费用
  if (
    props.selectedFee &&
    (oldRightsType === IRightsType.PERSONAL || oldRightsType === IRightsType.PERSONAL_LITE)
  ) {
    rememberedFees.value[oldRightsType] = props.selectedFee;
  }

  // 更新权益类型
  emit('update:currentRightsType', newRightsType);

  // 恢复目标权益类型对应的选中费用（如果有记忆）
  const rememberedFee = rememberedFees.value[newRightsType];
  if (rememberedFee) {
    // 验证记忆的费用是否在当前权益类型的费用列表中仍然存在
    const targetPlanRights =
      newRightsType === IRightsType.PERSONAL
        ? personalPlanRights.value
        : personalLitePlanRights.value;
    const feeExists = targetPlanRights?.rights_by_time.some(f => f.id === rememberedFee.id);
    if (feeExists) {
      emit('update:selectedFee', rememberedFee);
      return;
    }
  }

  // 如果没有记忆或记忆的费用不存在，清空选中
  emit('update:selectedFee', undefined);
}

// 选择费用卡片
function handleSelectFee(fee: IFees, rightsType: IRightsType) {
  const buriedService = getBuriedService();
  buriedService?.track('youyan_platform_payment_personal_package', {
    rights_type: rightsType,
    buy_type: fee.buy_type,
    days: fee.days,
    price: fee.price,
    time_identity: fee.time_identity,
  });

  // 保存当前选择到记忆
  if (rightsType === IRightsType.PERSONAL || rightsType === IRightsType.PERSONAL_LITE) {
    rememberedFees.value[rightsType] = fee;
  }

  emit('update:selectedFee', fee);
  emit('update:currentRightsType', rightsType);
}

const handleAddCoin = () => {
  paymentStore.handleBuyCredit();
};

// 默认选中全款年套餐（仅在没有任何选择且没有记忆时）
watch(
  currentPlanRights,
  newPlanRights => {
    if (!props.selectedFee && newPlanRights?.rights_by_time) {
      const currentType = currentRightsType.value;

      // 先检查是否有记忆
      const rememberedFee = rememberedFees.value[currentType];
      if (rememberedFee) {
        // 验证记忆的费用是否仍然存在
        const feeExists = newPlanRights.rights_by_time.some(f => f.id === rememberedFee.id);
        if (feeExists) {
          emit('update:selectedFee', rememberedFee);
          emit('update:currentRightsType', currentType);
          return;
        }
      }

      // 如果没有记忆或记忆的费用不存在，使用默认值（全款年套餐）
      const annualPaymentFee = newPlanRights.rights_by_time.find(
        f => f.buy_type === BUY_TYPE.PAYMENT && f.days === 365,
      );
      if (annualPaymentFee) {
        emit('update:selectedFee', annualPaymentFee);
        emit('update:currentRightsType', currentType);
        // 保存默认选择到记忆
        rememberedFees.value[currentType] = annualPaymentFee;
      }
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.content {
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  max-width: 672px;
  gap: 20px;
  padding: 20px 16px;
}

:deep(.rights-segmented) {
  width: 244px;
  margin: 0 auto;
}

.fees-list {
  display: flex;
  flex-direction: column;
  margin-top: 0;
}

.fees-list__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
</style>
